// Wiki links and tags in notes, and what they point at.
//
// Link forms:  [[Order]]  [[Order.Total]]  [[OrderingContext/Order]]  [[Idea/Invoice]] (in a sketch)
//              [[note:Title]]  [[sketch:Title]]  [[migration:AddSku]]  [[context:OrderingContext]]
//              [[target|label]]
// A plain [[Name]] is a note if one has that title, then a sketch, an entity (in a context's current
// model, then in a sketch), a migration or a context.
import type { ComplexSpec, EntitySpec, ModelSpec, OwnedSpec } from '../model/schema';

/** Which model an entity link points into (the app's ModelRef). */
export interface LinkModelRef {
  setKey: string;
  migrationId: string | null;
  sketchId?: string;
}

/** A note file's title: its first `# ` heading, or the file name. */
export function noteTitleOf(path: string, text: string): string {
  const heading = /^#\s+(.+?)\s*#*\s*$/m.exec(text);
  return heading?.[1] ?? path.slice(path.lastIndexOf('/') + 1).replace(/\.md$/, '');
}

export interface WikiLink {
  target: string;
  label: string | null;
  /** Offset of `[[` in the text. */
  from: number;
  to: number;
}

/** Text with code (fenced blocks and inline code) blanked out, so links and tags inside it are ignored. */
function withoutCode(text: string): string {
  const blank = (m: string) => m.replace(/[^\n]/g, ' ');
  return text.replace(/^(```|~~~)[^\n]*\n[\s\S]*?(^\1[^\n]*$|(?![\s\S]))/gm, blank).replace(/`[^`\n]*`/g, blank);
}

const LINK = /\[\[([^\]\n|<>]+?)(?:\|([^\]\n<>]+?))?\]\]/g;

export function parseLinks(text: string): WikiLink[] {
  const out: WikiLink[] = [];
  for (const m of withoutCode(text).matchAll(LINK)) {
    out.push({ target: m[1]!.trim(), label: m[2]?.trim() ?? null, from: m.index!, to: m.index! + m[0].length });
  }
  return out;
}

const TAG = /(^|[\s(])#([A-Za-z][\w/-]*)/g;

/** Tags (`#performance`, `#decision/accepted`), lower-cased, in order of first use. */
export function parseTags(text: string): string[] {
  const seen = new Set<string>();
  for (const m of withoutCode(text).matchAll(TAG)) seen.add(m[2]!.toLowerCase());
  return [...seen];
}

// ------------------------------------------------------------------ resolution

function propertyPaths(e: EntitySpec | OwnedSpec | ComplexSpec, prefix = ''): string[] {
  const out = Object.keys(e.properties).map((p) => `${prefix}${p}`);
  for (const [nav, o] of Object.entries(('owned' in e ? e.owned : undefined) ?? {})) out.push(`${prefix}${nav}`, ...propertyPaths(o, `${prefix}${nav}.`));
  for (const [name, c] of Object.entries(e.complex ?? {})) out.push(`${prefix}${name}`, ...propertyPaths(c, `${prefix}${name}.`));
  return out;
}

/** Entity keys to the names links can use after the dot: properties, owned and complex paths. */
export function linkableEntities(model: ModelSpec): Record<string, string[]> {
  return Object.fromEntries(Object.entries(model.entities).map(([k, e]) => [k, propertyPaths(e)]));
}

/** Owned and complex type names (`OrderLine`, `Money`) to where they first appear (`Order`, `Lines`). */
export function linkableTypes(model: ModelSpec): Record<string, { entity: string; path: string }> {
  const out: Record<string, { entity: string; path: string }> = {};
  const walk = (entity: string, c: EntitySpec | OwnedSpec | ComplexSpec, prefix: string) => {
    for (const [nav, o] of Object.entries(('owned' in c ? c.owned : undefined) ?? {})) {
      out[o.type] ??= { entity, path: `${prefix}${nav}` };
      walk(entity, o, `${prefix}${nav}.`);
    }
    for (const [name, x] of Object.entries(c.complex ?? {})) {
      out[x.type] ??= { entity, path: `${prefix}${name}` };
      walk(entity, x, `${prefix}${name}.`);
    }
  };
  for (const [key, e] of Object.entries(model.entities)) walk(key, e, '');
  return out;
}

export interface IndexedModel {
  ref: LinkModelRef;
  /** Context name, or the sketch title. */
  name: string;
  kind: 'context' | 'sketch';
  /** Entity keys to their property names (owned and complex paths included, like `Shipping.City`). */
  entities: Record<string, string[]>;
  /** Owned and complex type names, so `[[OrderLine]]` finds `Order.Lines`. */
  types?: Record<string, { entity: string; path: string }>;
}

export interface LinkIndex {
  notes: { id: string; title: string }[];
  sketches: { id: string; title: string; setKey: string | null }[];
  /** Current models of the contexts, then sketches. */
  models: IndexedModel[];
  migrations: { setKey: string; context: string; id: string; name: string }[];
}

export type LinkTarget =
  | { kind: 'note'; id: string; title: string }
  | { kind: 'sketch'; id: string; title: string }
  | { kind: 'entity'; ref: LinkModelRef; model: string; entity: string; property: string | null }
  | { kind: 'migration'; setKey: string; context: string; id: string; name: string }
  | { kind: 'context'; setKey: string; name: string };

const same = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;

function entityIn(model: IndexedModel, path: string): LinkTarget | null {
  const keys = Object.keys(model.entities);
  const entity = keys.find((k) => same(k, path));
  if (entity) return { kind: 'entity', ref: model.ref, model: model.name, entity, property: null };
  const type = Object.keys(model.types ?? {}).find((t) => same(t, path));
  if (type) return { kind: 'entity', ref: model.ref, model: model.name, entity: model.types![type]!.entity, property: model.types![type]!.path };
  // Entity.Property (the entity key itself has no dots; property paths may).
  const dot = path.indexOf('.');
  if (dot < 0) return null;
  const owner = keys.find((k) => same(k, path.slice(0, dot)));
  if (!owner) return null;
  const property = model.entities[owner]!.find((p) => same(p, path.slice(dot + 1)));
  return property ? { kind: 'entity', ref: model.ref, model: model.name, entity: owner, property } : null;
}

export function resolveLink(target: string, index: LinkIndex): LinkTarget | null {
  const colon = /^(note|sketch|migration|context|entity):(.+)$/i.exec(target);
  const prefix = colon?.[1]!.toLowerCase();
  const name = (colon ? colon[2]! : target).trim();
  const note = () => index.notes.find((n) => same(n.title, name));
  const sketch = () => index.sketches.find((s) => same(s.title, name));
  const migration = () => index.migrations.find((m) => same(m.name, name) || same(m.id, name));
  const context = () => index.models.find((m) => m.kind === 'context' && same(m.name, name));
  const entity = (): LinkTarget | null => {
    const slash = name.indexOf('/');
    if (slash > 0) {
      const scope = name.slice(0, slash);
      const model = index.models.find((m) => same(m.name, scope));
      return model ? entityIn(model, name.slice(slash + 1)) : null;
    }
    // Contexts first; an entity that exists only in designs is found in the first sketch that has it.
    for (const kind of ['context', 'sketch'] as const) {
      for (const model of index.models) {
        if (model.kind !== kind) continue;
        const found = entityIn(model, name);
        if (found) return found;
      }
    }
    return null;
  };
  const asNote = (n = note()) => (n ? ({ kind: 'note', id: n.id, title: n.title } as const) : null);
  const asSketch = (s = sketch()) => (s ? ({ kind: 'sketch', id: s.id, title: s.title } as const) : null);
  const asMigration = (m = migration()) => (m ? ({ kind: 'migration', ...m } as const) : null);
  const asContext = (c = context()) => (c ? ({ kind: 'context', setKey: c.ref.setKey, name: c.name } as const) : null);
  switch (prefix) {
    case 'note':
      return asNote();
    case 'sketch':
      return asSketch();
    case 'migration':
      return asMigration();
    case 'context':
      return asContext();
    case 'entity':
      return entity();
    default:
      return asNote() ?? asSketch() ?? entity() ?? asMigration() ?? asContext();
  }
}

/** Identity of a target, for backlinks: an entity counts with or without a property. */
export function targetKey(t: LinkTarget): string {
  switch (t.kind) {
    case 'note':
    case 'sketch':
      return `${t.kind}:${t.id}`;
    case 'entity':
      return `entity:${t.ref.sketchId ? `sketch:${t.ref.sketchId}` : t.ref.setKey}|${t.entity}`;
    case 'migration':
      return `migration:${t.setKey}|${t.id}`;
    case 'context':
      return `context:${t.setKey}`;
  }
}

export function targetLabel(t: LinkTarget): string {
  switch (t.kind) {
    case 'note':
    case 'sketch':
      return t.title;
    case 'entity':
      return t.property ? `${t.entity}.${t.property}` : t.entity;
    case 'migration':
      return t.name;
    case 'context':
      return t.name;
  }
}

export interface NoteLike {
  id: string;
  title: string;
  body: string;
}

/** Notes that link to `key` (a targetKey), with how often. */
export function backlinks(notes: NoteLike[], index: LinkIndex, key: string): { note: NoteLike; count: number }[] {
  const out: { note: NoteLike; count: number }[] = [];
  for (const note of notes) {
    const count = parseLinks(note.body).filter((l) => {
      const t = resolveLink(l.target, index);
      return t !== null && targetKey(t) === key;
    }).length;
    if (count) out.push({ note, count });
  }
  return out;
}
